import { CandidateRef } from "src/_common/utils/candidateRef";
import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { ACTIONS, URLS } from "src/_config";
import { useChatApi } from "src/_common/hooks/actions/chat/appChatApiHook";
import ChatWindow from "src/components/messages/ChatWindow";

interface ChatModalContextType {
  openChatModal: (jobseekerId: CandidateRef, jobseekerName: string) => void;
  closeChatModal: () => void;
  isOpen: boolean;
}

const ChatModalContext = createContext<ChatModalContextType | undefined>(undefined);

export const useChatModal = (): ChatModalContextType => {
  const context = useContext(ChatModalContext);
  if (!context) {
    throw new Error("useChatModal must be used within a ChatModalProvider");
  }
  return context;
};

interface ChatModalProviderProps {
  children: ReactNode;
}

export const ChatModalProvider: React.FC<ChatModalProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { callOpenConversation } = useChatApi();

  const [isOpen, setIsOpen] = useState(false);
  const [jobseekerId, setJobseekerId] = useState<CandidateRef | null>(null);
  const [jobseekerName, setJobseekerName] = useState<string>("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(false);

  const openChatModal = useCallback((id: CandidateRef, name: string) => {
    setJobseekerId(id);
    setJobseekerName(name);
    setIsOpen(true);
    setConversationId(null);
    setLoading(true);
    document.body.style.overflow = "hidden";

    callOpenConversation(
      { jobseeker_id: id },
      (_msg: string, data: any) => {
        const conv = data;
        if (conv) {
          setConversationId(conv.conversation_id);
          setIsBlocked(conv.is_blocked);
          dispatch({ type: ACTIONS.CHAT.SET_ACTIVE_CONVERSATION, payload: conv.conversation_id });
        }
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [callOpenConversation, dispatch]);

  const closeChatModal = useCallback(() => {
    setIsOpen(false);
    setJobseekerId(null);
    setJobseekerName("");
    setConversationId(null);
    document.body.style.overflow = "";
  }, []);

  const handleOpenFullChat = () => {
    if (conversationId) {
      history.push(`${URLS.CANDIDATE_ADVANCED_SEARCH.MAIN}?tab=message&conversation_id=${conversationId}`);
    }
    closeChatModal();
  };

  return (
    <ChatModalContext.Provider value={{ openChatModal, closeChatModal, isOpen }}>
      {children}

      {/* Global Chat Modal */}
      {isOpen && (
        <div className="drjobs-modal-overlay" onClick={closeChatModal}>
          <div
            className="drjobs-chat-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="drjobs-chat-modal__header">
              <div className="drjobs-chat-modal__title">
                <div className="drjobs-chat-modal__avatar">
                  {(jobseekerName || "J").charAt(0).toUpperCase()}
                </div>
                <span>{jobseekerName || "Jobseeker"}</span>
              </div>
              <div className="drjobs-chat-modal__header-actions">
                <button
                  className="drjobs-chat-modal__expand-btn"
                  onClick={handleOpenFullChat}
                  title="Open in Messages"
                >
                  <i className="fa fa-expand-alt" />
                </button>
                <button className="drjobs-chat-modal__close-btn" onClick={closeChatModal}>
                  <i className="fa fa-times" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="drjobs-chat-modal__body">
              {loading ? (
                <div className="drjobs-chat-modal__loading">
                  <i className="fa fa-spinner fa-spin" /> Opening conversation...
                </div>
              ) : conversationId ? (
                <ChatWindow
                  conversationId={conversationId}
                  isBlocked={isBlocked}
                  onBlockToggle={() => setIsBlocked(!isBlocked)}
                  compact
                />
              ) : (
                <div className="drjobs-chat-modal__error">Failed to open conversation</div>
              )}
            </div>

            {/* Footer */}
            {conversationId && (
              <div className="drjobs-chat-modal__footer">
                <button className="drjobs-chat-modal__full-btn" onClick={handleOpenFullChat}>
                  <i className="fa fa-external-link-alt" /> Open in Messages
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </ChatModalContext.Provider>
  );
};

export default ChatModalContext;
